import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import TextInput from "../../../main/components/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import SelectInput from "../../../main/components/SelectInput";
import { serviceRequestApi, usinasApi } from "../../../services/api";
import { MainContext } from "../../../main/context/MainContextProvider";
import { SismetroTipo } from "../../../services/api/routes/serviceRequest";
import {
  GetPatrimonioResponse,
  GravidadeType,
  TendenciaType,
  UrgenciaType,
} from "../types";
import SelectFreeSoloInput from "../../../main/components/SelectFreeSoloInput";
import { IoIosArrowBack } from "react-icons/io";
import { Usina } from "../../usinas/types";
import { useLocation } from "react-router-dom";
import { IntegrationError } from "../../../errors";
import BaseButton from "../../../main/components/BaseButton";

export type NewServiceRequestFormType = yup.InferType<typeof schema>;
const schema = yup.object().shape({
  usina: yup.string().required("Usina é obrigatório"),
  patrimonio: yup.string().required("Selecionar patrimônio é obrigatório"),
  urgencia: yup.string().required("Urgência é obrigatório"),
  tendencia: yup.string().nullable(),
  gravidade: yup.string().nullable(),
  tipo: yup.string().required("Tipo é obrigatório"),
  descricao: yup.string().required("Descrição é obrigatório"),
  servicosRealizar: yup.string().required("Serviços a realizar é obrigatório"),
});

export default function NewServiceRequest() {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const data = watch();

  const location = useLocation();
  const alarmUsinaIdFromState = location.state?.alarmUsinaId;

  const { user, selectedDashboardClient: selectedClient, selectedDashboardUsina: selectedUsina } = useContext(MainContext);

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [patrimonios, setPatrimonios] = useState<GetPatrimonioResponse[]>([]);
  const [newRequestId, setNewRequestId] = useState<number>();
  const [hasSismetroError, setHasSismetroError] = useState<boolean>(false);
  const [usinasByClient, setUsinasByClient] = useState<Usina[]>([]);
  const [loadingCreation, setLoadingCreation] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<string>("");
  const patrimoniosOptions = patrimonios.map((patrimonio) => ({
    value: `${patrimonio.id}`,
    label: `${patrimonio.name}`,
  }));
  const usinasWithSolicianteId = usinasByClient.filter(
    (usina) => usina.usinaIds.idSolicitante
  );
  const getUsinaValue = (usinaId: string, idSolicitante: string) => {
    return usinaId + "*" + idSolicitante
  }
  const usinasOptions = usinasWithSolicianteId.map((usina) => {
    return {
      value: getUsinaValue(usina.usinaIds.id, String(usina.usinaIds.idSolicitante)),
      label: usina.name ?? "",
    };
  });

  const onMount = async () => {
    setValue("descricao", `${data.descricao ? data.descricao : location.state?.alarmDescricao ?? ""}`);
    setValue("servicosRealizar", `${data.servicosRealizar ? data.servicosRealizar : location.state?.alarmSolutions ?? ""}`);
    try {
      if (data.usina) {
        const usina = usinasWithSolicianteId.find(
          (usina) => usina.usinaIds.id == data.usina.slice(0, data.usina.indexOf("*"))
        );
        const usinaIdSolicitante = Number(data.usina.slice(data.usina.indexOf("*") + 1));
        const usinaIdLocalizacao = usina?.usinaIds.idLocalizacao;
        const patrimonioResponse =
          await serviceRequestApi.getSismetroPatrimonio(
            usinaIdSolicitante,
            usinaIdLocalizacao
          );

        setPatrimonios(patrimonioResponse.data);
      }
    } catch (error) {
      throw new Error("Erro ao buscar dados para criação de SS");
    }
  };

  const getUsinas = async () => {
    try {
      const usinasResponse = await usinasApi.getUsinas(false);
      setUsinasByClient(usinasResponse.data);
    } catch (error) {
      throw new Error("Erro ao buscar usinas para criação de SS");
    }
  };

  useEffect(() => {
    const hasUsinaFromNavigate = alarmUsinaIdFromState !== undefined && alarmUsinaIdFromState !== null && alarmUsinaIdFromState !== "";

    if (hasUsinaFromNavigate) {
      const usinaFromNavigate = usinasWithSolicianteId.find(
        (usina) => Number(usina.usinaIds.ropInputId) === Number(alarmUsinaIdFromState)
      );

      if (usinaFromNavigate?.usinaIds.idSolicitante) {
        setValue(
          "usina",
          getUsinaValue(
            usinaFromNavigate.usinaIds.id,
            String(usinaFromNavigate.usinaIds.idSolicitante)
          )
        );
      }

      return;
    }

    if (selectedUsina?.usinaIds.idSolicitante) {
      setValue(
        "usina",
        getUsinaValue(selectedUsina.usinaIds.id, String(selectedUsina.usinaIds.idSolicitante))
      );
    }
  }, [
    alarmUsinaIdFromState,
    selectedUsina?.usinaIds.id,
    selectedUsina?.usinaIds.idSolicitante,
    setValue,
    usinasWithSolicianteId,
  ]);

  useEffect(() => {
    getUsinas();
  }, [selectedClient]);

  useEffect(() => {
    onMount();
  }, [data.usina, usinasByClient]);

  const onSubmit = async (data: NewServiceRequestFormType) => {
    setLoadingCreation(true);

    const postData = {
      idSolicitante: Number(data.usina.slice(data.usina.indexOf("*") + 1)) ?? null,
      idPatrimonio: Number(data.patrimonio) ?? null,
      urgencia: Number(data.urgencia),
      tendencia: Number(data.tendencia) === 0 ? null : Number(data.tendencia),
      gravidade: Number(data.gravidade) === 0 ? null : Number(data.gravidade),
      tipo: Number(data.tipo) ?? null,
      descricao: data.descricao ?? null,
      servicosRealizar: data.servicosRealizar ?? null,
      solicitadoPor: user?.name ?? null,
      usinaId: location.state?.alarmUsinaId,
      alarmCode: location.state?.alarmCode,
      alarmArea: location.state?.alarmArea,
      alarmUg: location.state?.alarmUg,
      alarmStartDate: location.state?.alarmLastActiveDate,
    };
    try {
      const sismetroResponse = await serviceRequestApi.postSismetro(postData);
      setNewRequestId(sismetroResponse.id);
      setHasSismetroError(sismetroResponse.hasSismetroError)
      setShowSuccessModal(true);
    } catch (error) {
      if (error instanceof IntegrationError) {
        setErrorDetails(error.message);
      }
      setShowErrorModal(true);
    } finally {
      setLoadingCreation(false);
    }
  };

  const navigate = useNavigate();

  const handleGoBack = () => {
    if (location.state?.navigateFrom == "center-operations") {
      navigate(-1);
      return;
    }

    if (location.state?.navigateFrom == "alarm-details") {
      navigate(
        `/alarm/details`, {
        state: {
          usinaName: location.state.alarmUsinaName ?? "",
          usinaId: location.state.alarmUsinaId ?? "",
          ug: location.state.alarmUg ?? "",
          area: location.state.alarmArea ?? "",
          code: location.state.alarmCode ?? "",
          string: location.state.alarmString && location.state.alarmString.includes("String") ? location.state.alarmString : "",
          message: location.state.alarmMessage ?? ""
        }
      }
      )
      return;
    }

    navigate("/service-request/list")
  }

  const tipos = [
    { value: `${SismetroTipo.Acompanhamento}`, label: "Acompanhamento" },
    { value: `${SismetroTipo.Calibracao}`, label: "Calibração" },
    { value: `${SismetroTipo.Conservacao}`, label: "Conservação" },
    { value: `${SismetroTipo.Corretiva}`, label: "Corretiva" },
    { value: `${SismetroTipo.Melhoria}`, label: "Melhoria" },
    { value: `${SismetroTipo.Monitoramento}`, label: "Monitoramento" },
    { value: `${SismetroTipo.Preditiva}`, label: "Preditiva" },
    { value: `${SismetroTipo.Preventiva}`, label: "Preventiva" },
    {
      value: `${SismetroTipo.ServicoAdministrativo}`,
      label: "Serviço Administrativo",
    },
    { value: `${SismetroTipo.Treinamento}`, label: "Treinamento" },
  ];

  const urgencia = [
    { value: `${UrgenciaType.PodeEperar}`, label: "1 - Pode esperar" },
    { value: `${UrgenciaType.PoucoUrgente}`, label: "2 - Pouco urgente" },
    {
      value: `${UrgenciaType.OMaisRapido}`,
      label: "3 - O mais rápido possível",
    },
    { value: `${UrgenciaType.Urgente}`, label: "4 - É urgente" },
    {
      value: `${UrgenciaType.NecessitaAcaoImediata}`,
      label: "5 - Necessita ação imediata",
    },
  ];

  const tendencia = [
    { value: "0", label: "Selecionar" },
    { value: `${TendenciaType.NaoIraPiorar}`, label: "1 - Não irá piorar" },
    {
      value: `${TendenciaType.IraPiorarLongoPrazo}`,
      label: "2 - Irá piorar a longo prazo",
    },
    {
      value: `${TendenciaType.IraPiorarMedioPrazo}`,
      label: "3 - Irá piorar em médio prazo",
    },
    {
      value: `${TendenciaType.IraPiorarPoucoTempo}`,
      label: "4 - Irá piorar em pouco tempo",
    },
    {
      value: `${TendenciaType.IraPiorarRapidamente}`,
      label: "5 - Irá piorar rapidamente",
    },
  ];

  const gravidade = [
    { value: "0", label: "Selecionar" },
    { value: `${GravidadeType.SemGravidade}`, label: "1 - Sem gravidade" },
    { value: `${GravidadeType.PoucoGrave}`, label: "2 - Pouco grave" },
    { value: `${GravidadeType.Grave}`, label: "3 - Grave" },
    { value: `${GravidadeType.MuitoGrave}`, label: "4 - Muito grave" },
    {
      value: `${GravidadeType.ExtremamenteGrave}`,
      label: "5 - Extremamente grave",
    },
  ];

  return (
    <Grid
      container
      component={"form"}
      spacing={1}
      onSubmit={handleSubmit(onSubmit)}
      // maxWidth={"900px"}
      justifyContent={"center"}
      padding={"20px"}
      marginBottom={"20px"}
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginRight: "50px",
          }}
        >
          <IconButton
            onClick={handleGoBack}
            sx={{
              width: {
                xs: "35px",
                sm: "40px",
              },
              height: {
                xs: "35px",
                sm: "40px",
              },
            }}
          >
            <IoIosArrowBack />
          </IconButton>
        </Box>
        <Typography
          sx={{
            marginRight: "10px",
            fontWeight: 500,
            fontSize: {
              xs: "18px",
              sm: "24px",
            },
          }}
        >
          {"Abertura de Solicitação de Serviço"}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <SelectInput
          control={control}
          fieldName={`usina`}
          label="Usina"
          errors={errors}
          sx={{
            width: "100%",
            marginBottom: "10px",
          }}
          options={usinasOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <Tooltip
          placement="top"
          title={data.usina ? "" : "Selecione uma usina!"}
        >
          <Box sx={{ width: "100%" }}>
            <SelectFreeSoloInput
              disabled={!data.usina}
              control={control}
              fieldName={`patrimonio`}
              label="Patrimônio"
              errors={errors}
              sx={{
                width: "100%",
                marginBottom: "10px",
              }}
              options={patrimoniosOptions}
            />
          </Box>
        </Tooltip>
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <SelectInput
          control={control}
          fieldName={`urgencia`}
          label="Urgência"
          errors={errors}
          sx={{
            width: "100%",
            // maxWidth: { xs: "300px", md: "250px" },
            marginBottom: "10px",
          }}
          options={urgencia}
        />
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <SelectInput
          control={control}
          fieldName={`tendencia`}
          label="Tendência"
          errors={errors}
          sx={{
            width: "100%",
            // maxWidth: { xs: "300px", md: "250px" },
            marginBottom: "10px",
          }}
          options={tendencia}
        />
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <SelectInput
          control={control}
          fieldName={`gravidade`}
          label="Gravidade"
          errors={errors}
          sx={{
            width: "100%",
            // maxWidth: { xs: "300px", md: "250px" },
            marginBottom: "10px",
          }}
          options={gravidade}
        />
      </Grid>
      <Grid item xs={12} sm={6} display={"flex"} md={4} xl={2}>
        <SelectInput
          control={control}
          fieldName={`tipo`}
          label="Tipo"
          errors={errors}
          sx={{
            width: "100%",
            // maxWidth: { xs: "300px", md: "250px" },
            marginBottom: "10px",
          }}
          options={tipos}
        />
      </Grid>
      <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
        <TextInput
          label={"Descrição"}
          control={control}
          errors={errors}
          multiline
          fieldName={"descricao"}
          sx={{
            width: "100%",
            marginBottom: "10px",
          }}
        />
        <BaseButton
          onClick={() => setValue("descricao", "")}
          sx={{ height: "35px", width: "fit-content" }}
          title="Limpar"
          loading={loadingCreation}
        />
      </Grid>
      <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
        <TextInput
          label={"Serviços a realizar"}
          control={control}
          errors={errors}
          multiline
          fieldName={"servicosRealizar"}
          sx={{
            width: "100%",
            marginBottom: "10px",
          }}
        />
        <BaseButton
          onClick={() => setValue("servicosRealizar", "")}
          sx={{ height: "35px", width: "fit-content" }}
          title="Limpar"
          loading={loadingCreation}
        />
      </Grid>
      <Grid item xs={12}>
        <PrimaryBaseButton
          type="submit"
          title="Confirmar dados"
          loading={loadingCreation}
        />
      </Grid>

      <ActionAlert
        message={!hasSismetroError ? "Solicitação criado com sucesso, você será redirecionado para o detalhe da sua solicitação" : "Solicitação criada com suceso"}
        severity="success"
        open={showSuccessModal}
        setOpen={setShowSuccessModal}
        onClose={() => {
          !hasSismetroError ? navigate(`/service-request/${newRequestId}`) : navigate("/service-request/list");
        }}
      />
      <ActionAlert
        message="Ocorreu um erro ao criar a solicitação de serviço, tente novamente"
        severity="error"
        errorDetails={errorDetails}
        open={showErrorModal}
        setOpen={setShowErrorModal}
        onClose={() => {
          setErrorDetails("");
        }}
      />
    </Grid>
  );
}
